import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertTitle, Box, Paper, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { setLoading, useMaterialUIController } from 'context';
import { apiErrorMessage } from '@functions/api-error-message';
import {
    ColumnMapping, IMPORT_FIELDS, SheetRow, SpreadsheetPersonRow, detectHeaderLine, guessMapping,
    readSpreadsheetRows, toPersonRows, withHeader,
} from '@functions/read-spreadsheet';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { CrmBoardService } from '@layouts/pages/CRM/kanban-board/services/crm-board.service';
import { PersonService } from '../services/person.service';
import { PersonImportRow } from '../services/requests/person.request';
import {
    EImportRowStatus, PeopleImportPreviewRow, PeopleImportResult,
} from '../services/responses/person.response';
import { ImportStep, ImportSteps } from './components/import-steps';
import { FIELD_LABEL, ImportColumnsStep } from './components/import-columns-step';
import { ConflictChoice, ImportReviewStep, defaultChoice } from './components/import-review-step';
import { ImportTarget } from './components/import-target';

/// Mesmo limite do servidor: conferir aqui evita mandar arquivo enorme só para ouvir não.
const MAX_ROWS = 2000;

/// O que entrou.
const DONE_KEYS = [
    ['resCriadas', 'created'],
    ['resSemTelefone', 'createdWithoutPhone'],
    ['resAtualizadas', 'updated'],
    ['resLeadsCriados', 'leadsCreated'],
    ['resLeadsReabertos', 'leadsReopened'],
] as const;

/// O que ficou de fora: vai em destaque, senão a tela parece dizer que tudo deu certo.
const SKIPPED_KEYS = [
    ['resLeadsPulados', 'leadsSkipped'],
    ['resLeadsSemTelefone', 'leadsWithoutPhone'],
    ['resConflitos', 'conflicts'],
    ['resRepetidas', 'repeated'],
    ['resInvalidas', 'invalid'],
] as const;

export const PeopleImport = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [controller, dispatch] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const fileInput = useRef<HTMLInputElement>(null);

    const [step, setStep] = useState<ImportStep>('file');
    const [fileName, setFileName] = useState('');
    const [allRows, setAllRows] = useState<SheetRow[]>([]);
    const [headerLine, setHeaderLine] = useState<number | null>(null);
    const [mapping, setMapping] = useState<ColumnMapping | null>(null);

    /// A etapa de colunas só entra se o palpite não achou nome e telefone, ou se pedirem ajuste.
    const [showColumns, setShowColumns] = useState(false);

    /// Colunas como estavam ao ajustar; voltar devolve a conferência sem o ajuste pela metade.
    const [reviewedColumns, setReviewedColumns] =
        useState<{ headerLine: number; mapping: ColumnMapping } | null>(null);

    const [withLeads, setWithLeads] = useState(false);
    const [boards, setBoards] = useState<{ id: string; name: string; isDefault: boolean }[]>([]);
    const [boardId, setBoardId] = useState<string | null>(null);
    const [columns, setColumns] = useState<{ id: string; name: string }[]>([]);
    const [columnId, setColumnId] = useState<string | null>(null);

    const [preview, setPreview] = useState<Map<number, PeopleImportPreviewRow>>(new Map());
    const [choices, setChoices] = useState<Record<number, ConflictChoice>>({});
    const [phones, setPhones] = useState<Record<number, string>>({});
    const [result, setResult] = useState<PeopleImportResult | null>(null);
    /// Fotografado ao importar: o interruptor pode mudar depois, mas o resultado é daquela importação.
    const [importedWithLeads, setImportedWithLeads] = useState(false);

    useEffect(() => {
        if (!withLeads || boards.length > 0) return;

        new CrmBoardService()
            .getSummaries()
            .then((res) => {
                const list = (res.data.data ?? []).filter((board) => board.active);
                setBoards(list);
                setBoardId((list.find((board) => board.isDefault) ?? list[0])?.id ?? null);
            })
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 4000));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [withLeads]);

    useEffect(() => {
        if (!boardId) return;

        setColumns([]);
        setColumnId(null);

        new CrmBoardService()
            .getById(boardId)
            .then((res) => {
                const list = (res.data.data?.columns ?? []).map((column) => ({ id: column.id, name: column.name }));
                setColumns(list);
                setColumnId(list[0]?.id ?? null);
            })
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 4000));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boardId]);

    const sheet = useMemo(
        () => (headerLine === null ? null : withHeader(allRows, headerLine)),
        [allRows, headerLine]
    );

    const baseRows = useMemo(
        () => (sheet && mapping ? toPersonRows(sheet, mapping) : []),
        [sheet, mapping]
    );

    /// As linhas como vão ao servidor: com o número corrigido na conferência no lugar do da planilha.
    const withPhones = (list: SpreadsheetPersonRow[], overrides: Record<number, string>) =>
        list.map((row) => (overrides[row.line] !== undefined ? { ...row, phone: overrides[row.line] } : row));

    const rows = useMemo(() => withPhones(baseRows, phones), [baseRows, phones]);

    const loadPreview = async (list: SpreadsheetPersonRow[]) => {
        setLoading(dispatch, true);

        try {
            const res = await new PersonService().previewImport(list);
            setPreview(new Map((res.data.data ?? []).map((item) => [item.line, item])));
            return true;
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
            return false;
        } finally {
            setLoading(dispatch, false);
        }
    };

    /// Trocar a linha do cabeçalho muda os nomes das colunas, então o palpite é refeito junto.
    const chooseHeader = (line: number) => {
        setHeaderLine(line);
        setMapping(guessMapping(withHeader(allRows, line).headers));
    };

    const chooseFile = async (file: File | undefined) => {
        if (!file) return;

        setLoading(dispatch, true);

        let read: SheetRow[];

        try {
            read = await readSpreadsheetRows(file);
        } catch {
            showAlert(t('people.importar.arquivoIlegivel'), 'error', 4000);
            return;
        } finally {
            setLoading(dispatch, false);
            if (fileInput.current) fileInput.current.value = '';
        }

        const line = detectHeaderLine(read);
        const below = line === null ? 0 : read.filter((row) => row.line > line).length;

        if (line === null || below === 0) {
            showAlert(t('people.importar.arquivoVazio'), 'warning', 4000);
            return;
        }

        if (below > MAX_ROWS) {
            showAlert(t('people.importar.muitasLinhas', { total: below, limite: MAX_ROWS }), 'warning', 5000);
            return;
        }

        const detected = withHeader(read, line);
        const guessed = guessMapping(detected.headers);

        setFileName(file.name);
        setAllRows(read);
        setHeaderLine(line);
        setMapping(guessed);
        setPreview(new Map());
        setChoices({});
        setPhones({});

        if (guessed.name === null || guessed.phone === null) {
            setShowColumns(true);
            setStep('columns');
            showAlert(t('people.importar.naoReconheci'), 'info', 5000);
            return;
        }

        setShowColumns(false);

        if (await loadPreview(toPersonRows(detected, guessed))) setStep('review');
    };

    const review = async () => {
        if (mapping?.name === null || mapping?.phone === null) {
            showAlert(t('people.importar.escolhaNomeTelefone'), 'warning', 3500);
            return;
        }

        if (rows.length === 0) {
            showAlert(t('people.importar.arquivoVazio'), 'warning', 4000);
            return;
        }

        if (await loadPreview(rows)) {
            setReviewedColumns(null);
            setStep('review');
        }
    };

    const adjustColumns = () => {
        setReviewedColumns({ headerLine, mapping });
        setShowColumns(true);
        setStep('columns');
    };

    const back = () => {
        if (step === 'columns' && reviewedColumns) {
            setHeaderLine(reviewedColumns.headerLine);
            setMapping(reviewedColumns.mapping);
            setReviewedColumns(null);
            setStep('review');
            return;
        }

        setStep(step === 'review' && showColumns ? 'columns' : 'file');
    };

    const choose = (line: number, choice: ConflictChoice) => {
        setChoices((current) => ({ ...current, [line]: choice }));

        // Desistir de corrigir o número devolve o da planilha, e a conferência precisa refletir isso.
        if (choice !== 'fix' && phones[line] !== undefined) {
            const next = { ...phones };
            delete next[line];
            setPhones(next);
            loadPreview(withPhones(baseRows, next));
        }
    };

    const decidable = (status: EImportRowStatus | undefined) =>
        status === EImportRowStatus.RepeatedInFile || status === EImportRowStatus.PhoneConflict;

    /// Linhas repetidas ou de outra pessoa que ficaram em "sem telefone" entram sem número.
    const toImport: PersonImportRow[] = rows.map((row) =>
        decidable(preview.get(row.line)?.status)
            && (choices[row.line] ?? defaultChoice(preview.get(row.line)?.status)) === 'nophone'
            ? { ...row, phone: '', withoutPhone: true }
            : row);

    const toWrite = toImport.filter((row) => {
        const status = preview.get(row.line)?.status;
        return status === EImportRowStatus.New || status === EImportRowStatus.SamePerson || row.withoutPhone;
    }).length;

    const runImport = async () => {
        if (withLeads && !columnId) {
            showAlert(t('people.importar.escolhaColuna'), 'warning', 3500);
            return;
        }

        setLoading(dispatch, true);

        try {
            const res = await new PersonService().import({
                rows: toImport,
                columnId: withLeads ? columnId : null,
            });

            setResult(res.data.data);
            setImportedWithLeads(withLeads);
            setStep('done');
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    const restart = () => {
        setStep('file');
        setShowColumns(false);
        setReviewedColumns(null);
        setFileName('');
        setAllRows([]);
        setHeaderLine(null);
        setMapping(null);
        setResult(null);
    };

    /// Campo do CRM em negrito e coluna da planilha entre aspas, para distingui-los.
    const mappingSummary = sheet && mapping
        ? IMPORT_FIELDS.filter((field) => mapping[field] !== null).map((field) => {
            const column = mapping[field] as number;
            const header = sheet.headers[column]
                || t('people.importar.colunaSemNome', { numero: column + 1 });

            return { field, header };
        })
        : [];

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('people.importar.titulo')} />

            <Paper sx={{ p: { xs: 2, md: 3 }, background: cc.cardBg, border: `1px solid ${cc.border}` }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 3,
                    }}
                >
                    <ImportSteps current={step} withColumns={showColumns} />
                    {fileName && (
                        <Typography sx={{ fontSize: 13, color: cc.textSecondary }}>{fileName}</Typography>
                    )}
                </Box>

                {step === 'file' && (
                    <Box
                        onClick={() => fileInput.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                            e.preventDefault();
                            chooseFile(e.dataTransfer.files?.[0]);
                        }}
                        sx={{
                            border: `2px dashed ${cc.border}`,
                            borderRadius: '12px',
                            py: 10,
                            textAlign: 'center',
                            cursor: 'pointer',
                            color: cc.textSecondary,
                            '&:hover': { borderColor: cc.accent, color: cc.accent },
                        }}
                    >
                        <UploadFileIcon sx={{ fontSize: '52px !important' }} />
                        <Typography sx={{ fontSize: 17, fontWeight: 600, mt: 1.5, color: 'inherit' }}>
                            {t('people.importar.escolherArquivo')}
                        </Typography>
                        <Typography sx={{ fontSize: 14, mt: 0.75, color: cc.textMuted }}>
                            {t('people.importar.ajuda')}
                        </Typography>
                        <input
                            ref={fileInput}
                            type="file"
                            hidden
                            accept=".xlsx,.xls,.csv"
                            onChange={(e) => chooseFile(e.target.files?.[0])}
                        />
                    </Box>
                )}

                {step === 'columns' && sheet && mapping && (
                    <ImportColumnsStep
                        allRows={allRows}
                        sheet={sheet}
                        mapping={mapping}
                        onHeaderLine={chooseHeader}
                        onMapping={setMapping}
                    />
                )}

                {step === 'review' && (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                                mb: 2.5,
                                px: 2,
                                py: 1.25,
                                borderRadius: '10px',
                                background: cc.surfaceBg,
                                border: `1px solid ${cc.border}`,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 240, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2.5, rowGap: 0.5 }}>
                                <Typography sx={{ fontSize: 13, color: cc.textMuted }}>
                                    {t('people.importar.usando')}
                                </Typography>
                                {mappingSummary.map(({ field, header }) => (
                                    <Typography key={field} sx={{ fontSize: 13, color: cc.textSecondary }}>
                                        <Box component="span" sx={{ fontWeight: 700, color: cc.textPrimary }}>
                                            {t(FIELD_LABEL[field])}
                                        </Box>{' '}
                                        {t('people.importar.vemDaColuna', { coluna: header })}
                                    </Typography>
                                ))}
                            </Box>
                            <MDButton
                                variant="text"
                                color="info"
                                size="small"
                                onClick={adjustColumns}
                            >
                                {t('people.importar.ajustarColunas')}
                            </MDButton>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <ImportTarget
                                withLeads={withLeads}
                                onWithLeads={setWithLeads}
                                boards={boards}
                                boardId={boardId}
                                onBoard={setBoardId}
                                columns={columns}
                                columnId={columnId}
                                onColumn={setColumnId}
                            />
                        </Box>

                        <ImportReviewStep
                            rows={rows}
                            preview={preview}
                            choices={choices}
                            phones={phones}
                            withLeads={withLeads}
                            boardId={boardId}
                            onChoice={choose}
                            onPhone={(line, phone) => setPhones((current) => ({ ...current, [line]: phone }))}
                            onPhoneDone={() => loadPreview(rows)}
                        />
                    </>
                )}

                {step === 'done' && result && (
                    <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: cc.textPrimary, mb: 1.5 }}>
                            {t('people.importar.resultado')}
                        </Typography>
                        {DONE_KEYS.filter(([, field]) => result[field] > 0).map(([key, field]) => (
                            <Typography key={key} sx={{ fontSize: 15, color: cc.textSecondary, py: 0.5 }}>
                                {t(`people.importar.${key}`, { total: result[field] })}
                            </Typography>
                        ))}

                        {SKIPPED_KEYS.some(([, field]) => result[field] > 0) && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <AlertTitle>
                                    {/* Pediu lead e não saiu nenhum: é isso que a pessoa precisa ler primeiro. */}
                                    {importedWithLeads && result.leadsCreated + result.leadsReopened === 0
                                        ? t('people.importar.nenhumLead')
                                        : t('people.importar.ficaramDeFora')}
                                </AlertTitle>
                                {SKIPPED_KEYS.filter(([, field]) => result[field] > 0).map(([key, field]) => (
                                    <Typography key={key} sx={{ fontSize: 14, py: 0.25 }}>
                                        {t(`people.importar.${key}`, { total: result[field] })}
                                    </Typography>
                                ))}
                            </Alert>
                        )}
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                        mt: 3,
                        pt: 2.5,
                        borderTop: `1px solid ${cc.divider}`,
                    }}
                >
                    {step === 'done' ? (
                        <>
                            <MDButton variant="gradient" color="dark" onClick={restart}>
                                {t('people.importar.outraPlanilha')}
                            </MDButton>
                            <MDButton variant="gradient" color="info" onClick={() => navigate('/crm/pessoas')}>
                                {t('people.importar.verPessoas')}
                            </MDButton>
                        </>
                    ) : (
                        <>
                            <MDButton variant="gradient" color="dark" onClick={() => navigate('/crm/pessoas')}>
                                {t('general.cancelar')}
                            </MDButton>

                            {step !== 'file' && (
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={back}
                                >
                                    {t('people.importar.voltar')}
                                </MDButton>
                            )}

                            {step === 'columns' && (
                                <MDButton variant="gradient" color="info" onClick={review}>
                                    {t('people.importar.conferir')}
                                </MDButton>
                            )}

                            {step === 'review' && (
                                <MDButton
                                    variant="gradient"
                                    color="success"
                                    onClick={runImport}
                                    disabled={toWrite === 0}
                                >
                                    {t('people.importar.importar', { total: toWrite })}
                                </MDButton>
                            )}
                        </>
                    )}
                </Box>
            </Paper>
        </DashboardLayout>
    );
};
